import { Api } from '@common/services/api';
import { getApiUrl } from '@common/services/api/utils';
import Storage from '@services/storage';

import {
  ACTIVITY_HEARTBEAT_MS,
  ACTIVITY_SESSION_STORAGE_KEY,
  type ActivitySessionReport,
  type BootDecision,
  type StoredActivitySession,
  buildActivitySessionPayload,
  decideBoot,
  isHiddenWithinGrace,
  isStoredActivitySession,
} from './session-logic';

interface PageSwapEventLike extends Event {
  activation?: { navigationType?: string } | null;
}

interface NavigateEventLike extends Event {
  navigationType?: string;
}

interface NavigationLike {
  addEventListener(type: 'navigate', listener: (event: NavigateEventLike) => void): void;
  removeEventListener(type: 'navigate', listener: (event: NavigateEventLike) => void): void;
}

let queue: Promise<void> = Promise.resolve();
let currentSession: StoredActivitySession | null = null;
let desiredOrganisationId: string | null = null;
let lifecycleGeneration = 0;
let heartbeatTimer: number | null = null;
let listenersBound = false;
let skipEndOnPageHide = false;

function enqueue(task: () => Promise<void>): Promise<void> {
  const run = queue.then(task, task);
  queue = run.then(() => undefined, () => undefined);
  return run;
}

function isDocumentReload(): boolean {
  if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') {
    return false;
  }

  const [entry] = performance.getEntriesByType('navigation');
  return (entry as PerformanceNavigationTiming | undefined)?.type === 'reload';
}

function readStoredSession(): StoredActivitySession | null {
  try {
    const raw = sessionStorage.getItem(ACTIVITY_SESSION_STORAGE_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    return isStoredActivitySession(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeStoredSession(session: StoredActivitySession) {
  try {
    sessionStorage.setItem(ACTIVITY_SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // sessionStorage can throw when it is full or blocked; the in-memory session still heartbeats.
  }
}

function clearStoredSession() {
  try {
    sessionStorage.removeItem(ACTIVITY_SESSION_STORAGE_KEY);
  } catch {
    // Ignore quota and privacy-mode failures; memory state is cleared by the caller.
  }
}

function sessionEndpoint(organisationId: string) {
  return `/v1/organisations/${encodeURIComponent(organisationId)}/activity/sessions`;
}

async function deliverSession(
  organisationId: string,
  report: ActivitySessionReport,
): Promise<void> {
  try {
    await Api.post(sessionEndpoint(organisationId), { sessions: [report] });
  } catch {
    // A lost heartbeat is replaced by the next one. A lost end is closed by the nightly job.
  }
}

function postSessionNow(organisationId: string, report: ActivitySessionReport) {
  const token = Storage.getCookie('access_token');
  if (!token || typeof fetch !== 'function') return;

  // fetch must be issued inside pagehide. Awaiting headers first lets the browser cancel it.
  // sendBeacon cannot set X-API-Token.
  void fetch(getApiUrl(sessionEndpoint(organisationId)), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-API-Token': token,
    },
    credentials: 'include',
    keepalive: true,
    body: JSON.stringify({ sessions: [report] }),
  }).catch(() => undefined);
}

function stopHeartbeat() {
  if (heartbeatTimer === null) return;
  window.clearInterval(heartbeatTimer);
  heartbeatTimer = null;
}

function syncHeartbeat() {
  stopHeartbeat();
  if (!currentSession || desiredOrganisationId === null) return;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

  heartbeatTimer = window.setInterval(() => {
    const session = currentSession;
    if (!session || desiredOrganisationId === null) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

    const seenAt = new Date().toISOString();
    void enqueue(async () => {
      if (!currentSession || currentSession.uuid !== session.uuid) return;
      if (desiredOrganisationId === null) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

      await deliverSession(
        session.organisationId,
        buildActivitySessionPayload(session, seenAt),
      );
    });
  }, ACTIVITY_HEARTBEAT_MS);
}

function createSessionUuid(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID();

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === 'x' ? random : ((random % 4) + 8);
    return value.toString(16);
  });
}

async function openSession(organisationId: string, startedAt: string, generation: number) {
  if (desiredOrganisationId !== organisationId || generation !== lifecycleGeneration) return;

  const session: StoredActivitySession = {
    uuid: createSessionUuid(),
    organisationId,
    startedAt,
    hiddenAt: null,
  };

  const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
  const stored: StoredActivitySession = hidden
    ? { ...session, hiddenAt: startedAt }
    : session;

  currentSession = stored;
  writeStoredSession(stored);
  await deliverSession(
    organisationId,
    buildActivitySessionPayload(stored, startedAt),
  );

  if (desiredOrganisationId !== organisationId || generation !== lifecycleGeneration) return;
  if (!hidden) syncHeartbeat();
}

async function activate(organisationId: string) {
  const generation = lifecycleGeneration;
  if (desiredOrganisationId !== organisationId) return;

  bindLifecycle();

  const nowMs = Date.now();
  const nowIso = new Date(nowMs).toISOString();
  const decision: BootDecision = decideBoot({
    stored: readStoredSession(),
    organisationId,
    nowIso,
    nowMs,
    // A duplicated tab copies sessionStorage, and a fresh in-memory id is new for both
    // a reload and a duplicate. Only a reload navigation may adopt the stored uuid.
    // bfcache restore keeps this document and is handled by pageshow.
    adoptStoredSession: isDocumentReload(),
  });

  if (decision.type === 'end-then-start') {
    await deliverSession(
      decision.previous.organisationId,
      buildActivitySessionPayload(decision.previous, decision.endedAt, decision.endedAt),
    );
    if (desiredOrganisationId !== organisationId || generation !== lifecycleGeneration) return;
    await openSession(organisationId, nowIso, generation);
    return;
  }

  if (decision.type === 'start') {
    await openSession(organisationId, nowIso, generation);
    return;
  }

  const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
  const hiddenAt = hidden
    ? (decision.session.hiddenAt ?? nowIso)
    : null;
  const session: StoredActivitySession = { ...decision.session, hiddenAt };
  currentSession = session;
  writeStoredSession(session);

  const seenAt = hiddenAt ?? nowIso;
  await deliverSession(
    organisationId,
    buildActivitySessionPayload(session, seenAt),
  );

  if (desiredOrganisationId !== organisationId || generation !== lifecycleGeneration) return;
  if (!hidden) syncHeartbeat();
}

async function hideSession() {
  if (!currentSession || desiredOrganisationId === null) return;

  stopHeartbeat();
  const hiddenAt = new Date().toISOString();
  currentSession = { ...currentSession, hiddenAt };
  writeStoredSession(currentSession);
  await deliverSession(
    currentSession.organisationId,
    buildActivitySessionPayload(currentSession, hiddenAt),
  );
}

async function showSession() {
  if (!currentSession || desiredOrganisationId === null) return;

  const nowMs = Date.now();
  const nowIso = new Date(nowMs).toISOString();
  if (isHiddenWithinGrace(currentSession.hiddenAt, nowMs)) {
    currentSession = { ...currentSession, hiddenAt: null };
    writeStoredSession(currentSession);
    syncHeartbeat();
    return;
  }

  const endedAt = currentSession.hiddenAt ?? nowIso;
  const previous = currentSession;
  await deliverSession(
    previous.organisationId,
    buildActivitySessionPayload(previous, endedAt, endedAt),
  );

  const organisationId = desiredOrganisationId;
  if (!organisationId) return;
  await openSession(organisationId, new Date().toISOString(), lifecycleGeneration);
}

function sessionEndTimestamp(session: StoredActivitySession, nowIso: string): string {
  if (!session.hiddenAt) return nowIso;

  const hiddenMs = Date.parse(session.hiddenAt);
  if (Number.isNaN(hiddenMs)) return nowIso;

  return session.hiddenAt;
}

async function deactivate() {
  stopHeartbeat();
  const session = currentSession ?? readStoredSession();
  currentSession = null;
  clearStoredSession();
  if (!session) return;

  const endedAt = sessionEndTimestamp(session, new Date().toISOString());
  await deliverSession(
    session.organisationId,
    buildActivitySessionPayload(session, endedAt, endedAt),
  );
}

function onVisibilityChange() {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    stopHeartbeat();
    void enqueue(hideSession);
    return;
  }

  void enqueue(showSession);
}

function onPageHide(event: Event) {
  const persisted = (event as PageTransitionEvent).persisted === true;
  const skipEnd = skipEndOnPageHide || persisted;
  skipEndOnPageHide = false;

  if (skipEnd) {
    if (persisted) stopHeartbeat();
    return;
  }

  lifecycleGeneration += 1;
  desiredOrganisationId = null;
  stopHeartbeat();

  const session = currentSession ?? readStoredSession();
  currentSession = null;
  clearStoredSession();
  if (!session) return;

  const endedAt = sessionEndTimestamp(session, new Date().toISOString());
  postSessionNow(
    session.organisationId,
    buildActivitySessionPayload(session, endedAt, endedAt),
  );
}

function onPageShow(event: Event) {
  if ((event as PageTransitionEvent).persisted !== true) return;
  if (!currentSession) currentSession = readStoredSession();
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  void enqueue(showSession);
}

function onPageSwap(event: Event) {
  // pagehide also runs on reload. Ending there closes the session, and the server
  // will not reopen it, so the same session_uuid could not continue after a refresh.
  const navigationType = (event as PageSwapEventLike).activation?.navigationType;
  if (navigationType === 'reload') skipEndOnPageHide = true;
}

function onNavigate(event: NavigateEventLike) {
  if (event.navigationType === 'reload') skipEndOnPageHide = true;
}

function getNavigation(): NavigationLike | null {
  if (typeof window === 'undefined') return null;
  const { navigation } = (window as Window & { navigation?: NavigationLike });
  if (!navigation || typeof navigation.addEventListener !== 'function') return null;
  return navigation;
}

function bindLifecycle() {
  if (listenersBound || typeof window === 'undefined' || typeof document === 'undefined') return;

  listenersBound = true;
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('pageshow', onPageShow);
  window.addEventListener('pageswap', onPageSwap);
  getNavigation()?.addEventListener('navigate', onNavigate);
}

function unbindLifecycle() {
  if (!listenersBound || typeof window === 'undefined' || typeof document === 'undefined') {
    listenersBound = false;
    return;
  }

  document.removeEventListener('visibilitychange', onVisibilityChange);
  window.removeEventListener('pagehide', onPageHide);
  window.removeEventListener('pageshow', onPageShow);
  window.removeEventListener('pageswap', onPageSwap);
  getNavigation()?.removeEventListener('navigate', onNavigate);
  listenersBound = false;
}

export function startActivitySession(organisationId: string): Promise<void> {
  if (!organisationId) return Promise.resolve();

  desiredOrganisationId = organisationId;
  return enqueue(() => activate(organisationId)).catch(() => undefined);
}

export function endActivitySession(): Promise<void> {
  desiredOrganisationId = null;
  lifecycleGeneration += 1;
  return enqueue(deactivate).catch(() => undefined);
}

export function resetActivitySessionForTests() {
  stopHeartbeat();
  unbindLifecycle();
  currentSession = null;
  desiredOrganisationId = null;
  skipEndOnPageHide = false;
  lifecycleGeneration += 1;
  queue = Promise.resolve();
  clearStoredSession();
}
